'use client';
import React, { ReactNode } from 'react';
import { useRouter } from 'next/navigation';

import Button from '@/common/components/ui/Button';
import { QuizQuestionData } from 'quiz-types';
import ActivityNavButtons from '@/(portal)/student/lesson/screens/components/ActivityNavButtons';
import { CloseIcon } from '@/assets/icons/button';
import { CheckmarkCircleIcon } from '@/assets/ui';
import { getInputClassName } from '@/common/components/input/StandardInputStyles';
import { useAppDispatch } from '@/lib/hooks';
import AuroraUtilityService from '@/services/util/aurora-utililty-service';
import { setSetName } from '@/lib/slices/study-slice';
import ActiveStudyCacheService from '@/services/storage/active-study-cache-service';

interface Props {
  // TODO: add type
  submitData: {
    grade: number;
    MCData: Array<{ answerChosen: number; correct: boolean }>;
    FRData: Array<number | string>;
  };
  questionSet: Array<QuizQuestionData>;
  answers: Record<number, number | string>;
  quizName?: string;
}

const QuizReview = ({ submitData, questionSet, answers }: Props) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  // Build a topics string from the quiz questions to guide flashcard generation
  const buildTopicsFromQuiz = (): string => {
    const questions = (questionSet || [])
      .map((q) => (q?.question || '').trim())
      .filter((q) => q.length > 0);

    if (questions.length === 0) return 'General review of recent quiz topics';

    // Join questions; cap the total length to avoid overly long prompts
    const joined = questions.join('; ');
    const maxLen = 800;
    const trimmed = joined.length > maxLen ? joined.slice(0, maxLen) : joined;
    return `Generate concise, high-quality study flashcards based on these quiz questions: ${trimmed}`;
  };

  const handleGenerateFlashcards = async () => {
    const topics = buildTopicsFromQuiz();
    // Navigate first so the user lands on the flashcards page (which shows loading state)
    router.push('/student/study/flashcards');
    // Kick off generation (updates global study state and cache)
    await AuroraUtilityService.generateFlashcards(topics, dispatch);
  };

  const renderMC = (questionIndex: number): ReactNode => {
    const questionData = questionSet[questionIndex];

    if (!questionData.options) {
      return <span>Content not found. Please reload.</span>;
    }

    return (
      <div className='w-full'>
        <div className='break-words text-start font-semibold text-base leading-6 tracking-[0%]'>
          {questionData.question}
        </div>
        <div className='mt-2 flex items-center justify-start gap-2'>
          <CheckmarkCircleIcon
            className={`w-6 h-6 bg-success rounded-full p-1 ${(submitData.MCData[questionIndex].correct ?? true) ? '' : 'hidden'}`}
          />
          <CloseIcon
            className={`w-6 h-6 fill-current rounded-full ${(submitData.MCData[questionIndex].correct ?? true) ? 'hidden' : ''}`}
          />
          <span className='px-3 py-2 border-2 border-border_gray_200 rounded-md text-base leading-6'>
            {
              questionData.options.find((option) => option.index == answers[questionData.index])
                ?.value
            }
          </span>
        </div>
      </div>
    );
  };

  const renderSA = (questionIndex: number): ReactNode => {
    const questionData = questionSet[questionIndex];
    const answerData = answers[questionIndex];
    if (!questionData) {
      return <span>Content not found. Please reload.</span>;
    }

    return (
      <div className='w-full'>
        <div className='break-words text-start font-semibold text-base leading-6 tracking-[0%]'>
          {questionData.question}
        </div>
        <div className='mt-2'>
          <input
            placeholder='Answer question here...'
            type='text'
            className={getInputClassName('text') + ' cursor-default'}
            value={`${answerData ?? ''}`}
            readOnly
            aria-readonly='true'
          />
        </div>
      </div>
    );
  };

  const renderFR = (questionIndex: number): ReactNode => {
    const questionData = questionSet[questionIndex];
    const answerData = answers[questionIndex];

    if (!questionData) {
      return <span>Content not found. Please reload.</span>;
    }

    return (
      <div className='w-full'>
        <div className='break-words text-start font-semibold text-base leading-6 tracking-[0%] cursor-default'>
          {questionData.question}
        </div>
        <div className='mt-2'>
          <textarea
            placeholder='Answer question here...'
            className={`${getInputClassName('textarea')} cursor-default resize-none min-h-[8rem]`}
            value={`${answerData ?? ''}`}
            readOnly
            aria-readonly='true'
          />
        </div>
      </div>
    );
  };

  const renderQuestionData = (questionIndex: number): ReactNode => {
    const questionData = questionSet[questionIndex];

    switch (questionData.type) {
      case 'Multiple Choice':
        return renderMC(questionIndex);
      case 'Short Answer':
        return renderSA(questionIndex);
      case 'Paragraph':
        return renderFR(questionIndex);
      default:
        return <span>Content not found. Please reload.</span>;
    }
  };

  return (
    <div className='flex h-full mx-auto w-full lg:max-w-[1200px] md:max-w-[720px]'>
      <div className='flex flex-col flex-grow'>
        <div className='flex flex-col bg-surface_gray_50 border border-border_gray_200 drop-shadow-md p-6 pb-12 rounded-xl gap-6'>
          <h2 className='text-2xl font-semibold text-center'>Quiz Results</h2>
          <div className='flex flex-col gap-6'>
            {questionSet.map((question, index) => (
              <div key={index}>{renderQuestionData(index)}</div>
            ))}
          </div>
        </div>
        {/* Spacer to prevent overlap with fixed ActivityNavButtons */}
        <div className='h-[80px] flex-none' />
      </div>
      <ActivityNavButtons />
    </div>
  );
};

export default QuizReview;
